import { useQuery } from '@apollo/client';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import USERS_BY_EMAIL_QUERY from 'gql/queries/EventService/getUsersEmail.gql';

import { GQLGuard } from 'app/components';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';

import { ModuleSelection, ObserverType } from '../../models';

interface Options {
  id: number;
  email: string;
}

//! This component is also used in the Article Creation and AWPM Creation
export const ObserverUserAutocomplete = () => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Options[]>([]);
  const [loading, setLoading] = useState(false);

  const { setFieldValue, values } = useFormikContext<ModuleSelection>();

  const ids = values.observers
    .filter(org => org.observerType === ObserverType.USER)
    .map(org => org.refId);

  const getUsersQuery = useQuery<
    { users: Options[] },
    { ids?: number[]; filter?: string; type?: UserType }
  >(USERS_BY_EMAIL_QUERY, {
    context: { clientName: 'federation' },
    variables: { ids: ids, type: UserType.SHIBBOLETH }
  });

  useEffect(() => {
    setLoading(getUsersQuery.loading);
    if (!!getUsersQuery.data) {
      setOptions([...getUsersQuery.data?.users]);
    }
  }, [getUsersQuery]);

  const changeHandler = value => {
    if (value !== '') {
      const filter: string = JSON.stringify({ email: { term: value } });
      getUsersQuery.refetch({ ids: undefined, filter: filter, type: UserType.SHIBBOLETH });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceChangeHandler = useCallback(
    debounce(value => {
      changeHandler(value);
    }, 500),
    []
  );

  const handleChange = (event, value: Options[]) => {
    return setFieldValue('observers', [
      ...values.observers.filter(org => org.observerType !== ObserverType.USER),
      ...value.map(val => ({
        refId: val.id,
        observerType: ObserverType.USER
      }))
    ]);
  };

  return (
    <GQLGuard query={getUsersQuery}>
      {data => {
        return (
          <Autocomplete
            multiple
            defaultValue={[
              ...values.observers
                .filter(org => org.observerType === ObserverType.USER)
                .map(org => {
                  return {
                    id: org.refId,
                    email: data.users.find(selOpt => selOpt.id === org.refId)?.email ?? ''
                  };
                })
            ]}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onInputChange={(event, value) => {
              debounceChangeHandler(value);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.email}
            onChange={handleChange}
            options={options}
            loading={loading}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t(translations.common.enterEmail())}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
              />
            )}
          />
        );
      }}
    </GQLGuard>
  );
};
